import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  Shield,
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Download,
  User,
  Mail,
  Calendar
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import AddAdminModal from '../../components/AddAdminModal';

const Roles = () => {
  const { hasPermission } = useAuth();
  
  const defaultFilters = {
    search: '',
    role: '',
    isActive: '',
    page: 1,
    limit: 10
  };

  const {
    filters,
    handleFilterChange,
    handlePageChange,
    clearFilters
  } = usePersistentFilters(defaultFilters, 'roles-filters');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [loadingAdminId, setLoadingAdminId] = useState(null);

  const { data, isLoading, error, refetch } = useQuery(
    ['roles', filters],
    () => api.get('/roles', { params: filters }),
    {
      keepPreviousData: true
    }
  );

  const deleteMutation = useMutation(
    (adminId) => api.delete(`/roles/${adminId}`),
    {
      onSuccess: () => {
        toast.success('Admin deleted successfully');
        refetch();
      },
      onError: () => {
        toast.error('Failed to delete admin');
      }
    }
  );

  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      deleteMutation.mutate(adminId);
    }
  };

  const handleEditAdmin = async (admin) => {
    try {
      console.log('✏️ Edit admin clicked, fetching details for ID:', admin._id);
      setLoadingAdminId(admin._id);
      const response = await api.get(`/roles/${admin._id}`);
      const detailedAdmin = response.data?.data;
      setEditingAdmin(detailedAdmin || admin);
      setShowAddModal(true);
    } catch (err) {
      console.error('Failed to fetch admin details:', err);
      toast.error('Failed to fetch admin details');
      setEditingAdmin(admin);
      setShowAddModal(true);
    } finally {
      setLoadingAdminId(null);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingAdmin(null);
  };

  if (error) {
    toast.error('Failed to load admins');
  }

  const admins = data?.data?.data?.admins || [];
  const pagination = data?.data?.data?.pagination || {};

  const getRoleBadge = (role) => {
    const roleConfig = {
      'SUPER_ADMIN': { class: 'badge-danger', text: 'Super Admin' },
      'ADMIN': { class: 'badge-primary', text: 'Admin' },
      'MODERATOR': { class: 'badge-warning', text: 'Moderator' }
    };

    const config = roleConfig[role] || { class: 'badge-info', text: role };
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="badge badge-success">Active</span>
    ) : (
      <span className="badge badge-danger">Inactive</span>
    );
  };

  if (!hasPermission('roles', 'view')) {
    return (
      <div className="empty-state">
        <Shield className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view roles and permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Role Management</h1>
              <p className="page-subtitle">
                Manage admin roles and permissions
              </p>
            </div>
            {hasPermission('roles', 'write') && (
              <button 
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Admin
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">Filters</h3>
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <label className="form-label">Role</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="input"
              >
                <option value="">All Roles</option>
                <option value="SUPER_ADMIN">Super Admin</option>
                <option value="ADMIN">Admin</option>
                <option value="MODERATOR">Moderator</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Admins Table */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Admins ({pagination.totalRecords || 0})
            </h3>
            <button className="btn btn-outline">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : admins.length === 0 ? (
            <div className="empty-state">
              <Shield className="empty-state-icon" />
              <h3 className="empty-state-title">No admins found</h3>
              <p className="empty-state-description">
                No admins match your current filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Admin</th>
                    <th className="table-header-cell">Contact</th>
                    <th className="table-header-cell">Role</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Created By</th>
                    <th className="table-header-cell">Last Login</th>
                    <th className="table-header-cell">Created</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {admins.map((admin) => (
                    <tr key={admin._id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600">
                                {admin.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                            <div className="text-sm text-gray-500">{admin.email}</div>
                            {admin.designation && (
                              <div className="text-xs text-gray-400">{admin.designation}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                      </td>
                      <td className="table-cell">
                        {getRoleBadge(admin.role)}
                      </td>
                      <td className="table-cell">
                        {getStatusBadge(admin.isActive)}
                      </td>
                      <td className="table-cell">
                        {admin.createdBy ? (
                          <div className="text-sm text-gray-900">
                            {admin.createdBy.name}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">N/A</span>
                        )}
                      </td>
                      <td className="table-cell">
                        {admin.lastLogin ? (
                          <div className="text-sm text-gray-900">
                            {new Date(admin.lastLogin).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Never</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-gray-900">
                          {new Date(admin.createdOn).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          {hasPermission('roles', 'write') && (
                            <button
                              onClick={() => handleEditAdmin(admin)}
                              disabled={loadingAdminId === admin._id}
                              className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                              title="Edit Admin"
                            >
                              {loadingAdminId === admin._id ? (
                                <span className="animate-spin text-xs">⏳</span>
                              ) : (
                                <Edit className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          
                          {hasPermission('roles', 'delete') && admin.role !== 'SUPER_ADMIN' && (
                            <button
                              onClick={() => handleDeleteAdmin(admin._id)}
                              className="text-danger-600 hover:text-danger-900"
                              title="Delete Admin"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
            {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
            {pagination.totalRecords} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={pagination.current === 1}
              className="btn btn-outline btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={pagination.current === pagination.total}
              className="btn btn-outline btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Permissions Overview */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Permissions Overview</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { module: 'Dashboard', icon: '📊', description: 'View dashboard statistics and analytics' },
              { module: 'Drivers', icon: '🚗', description: 'Manage driver accounts and approvals' },
              { module: 'Passengers', icon: '👥', description: 'Manage passenger accounts and activities' },
              { module: 'Trips', icon: '📍', description: 'Monitor and manage ride trips' },
              { module: 'Roles', icon: '🛡️', description: 'Manage admin roles and permissions' },
              { module: 'Support', icon: '💬', description: 'Handle customer support queries' },
              { module: 'Fare', icon: '💰', description: 'Configure fare settings and pricing' },
              { module: 'Settings', icon: '⚙️', description: 'Manage system settings and configuration' }
            ].map((permission) => (
              <div key={permission.module} className="border rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{permission.icon}</span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{permission.module}</h4>
                    <p className="text-xs text-gray-500">{permission.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Admin Modal */}
      <AddAdminModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        admin={editingAdmin}
        isEdit={!!editingAdmin}
      />
    </div>
  );
};

export default Roles; 